/**
 * Gregorian ↔ Hijri (lunar Islamic) dates through `Intl.DateTimeFormat`.
 *
 * Two calendars, both built into every current browser's ICU data:
 *
 * - `islamic-umalqura` — Saudi Arabia's official civil calendar. ICU holds the published
 *   Umm al-Qura month lengths as a table for AH 1300–1600 (1882–2174 CE) and falls back to
 *   the tabular calendar outside it. Checked against R. H. van Gent's reconstruction (Utrecht
 *   University, webspace.science.uu.nl/~gent0113/islam/ummalqura.htm): all 155 principal
 *   observance dates for AH 1420–1450 agree. Since 1423 AH a month starts on the evening of
 *   the 29th when, at Mecca, the conjunction comes before sunset and the moon sets after
 *   the sun; otherwise the month runs to 30 days.
 * - `islamic-civil` — the arithmetical (tabular) calendar: 30-year cycles with leap years
 *   2, 5, 7, 10, 13, 16, 18, 21, 24, 26 and 29, counted from the "civil" epoch, Friday
 *   16 July 622 (Julian).
 *
 * `Intl` formats a date into the Hijri calendar but cannot parse one back, so Hijri →
 * Gregorian estimates the day from the mean year and month lengths and walks to the first
 * day of the target month, then checks the day exists. Day counts are days since
 * 1970-01-01 in the proleptic Gregorian calendar, shared with the week-number tool.
 */

import {
  type CivilDate,
  civilFromDays,
  daysFromCivil,
  dayToUtcDate,
} from "@/lib/tool-kit/converters/week-number";

export type HijriCalendar = "islamic-umalqura" | "islamic-civil";

export interface HijriDate {
  year: number;
  month: number;
  day: number;
}

/** 1 Muharram AH 1 in the civil (Friday) epoch: 16 July 622 Julian = 19 July 622 proleptic Gregorian. */
export const HIJRI_EPOCH_DAYS = daysFromCivil(622, 7, 19);
/** 31 December 9999 — the last date a date field accepts. */
export const LAST_SUPPORTED_DAYS = daysFromCivil(9999, 12, 31);
export const MAX_HIJRI_YEAR = 9600;
/** The first day of the Gregorian calendar; earlier dates also get a Julian equivalent. */
export const GREGORIAN_REFORM_DAYS = daysFromCivil(1582, 10, 15);
/** The years ICU's Umm al-Qura table covers. */
export const UMM_AL_QURA_FIRST_YEAR = 1300;
export const UMM_AL_QURA_LAST_YEAR = 1600;

/**
 * Mean tabular year and mean synodic month, in days. A 30-year tabular cycle is 30
 * years of 354 days plus 11 leap days.
 */
const MEAN_YEAR = (30 * 354 + 11) / 30;
const MEAN_MONTH = 29.530_589;

const formatters = new Map<HijriCalendar, Intl.DateTimeFormat>();

function numericFormatter(calendar: HijriCalendar): Intl.DateTimeFormat {
  let formatter = formatters.get(calendar);
  if (!formatter) {
    // Latin digits and English parts: this formatter is plumbing whose output is re-parsed.
    formatter = new Intl.DateTimeFormat(`en-u-ca-${calendar}-nu-latn`, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "UTC",
    });
    formatters.set(calendar, formatter);
  }
  return formatter;
}

/** False on an engine whose ICU data lacks the calendar (it would silently format Gregorian). */
export function isCalendarSupported(calendar: HijriCalendar): boolean {
  try {
    return numericFormatter(calendar).resolvedOptions().calendar === calendar;
  } catch {
    return false;
  }
}

/** The Hijri date of a day count. */
export function toHijri(days: number, calendar: HijriCalendar): HijriDate {
  const result: HijriDate = { year: 0, month: 0, day: 0 };
  for (const part of numericFormatter(calendar).formatToParts(
    dayToUtcDate(days)
  )) {
    if (part.type === "year" || part.type === "month" || part.type === "day") {
      result[part.type] = Number(part.value);
    }
  }
  return result;
}

function estimateDays(year: number, month: number): number {
  return (
    HIJRI_EPOCH_DAYS +
    Math.floor((year - 1) * MEAN_YEAR + (month - 1) * MEAN_MONTH)
  );
}

/**
 * The day count of the first day of a Hijri month.
 *
 * The estimate lands within a few days of the answer; each step then jumps across at least
 * one month boundary toward the target, so the loop settles in one or two steps.
 */
export function firstOfMonth(
  year: number,
  month: number,
  calendar: HijriCalendar
): number {
  let days = estimateDays(year, month);
  for (let guard = 0; guard < 24; guard++) {
    const found = toHijri(days, calendar);
    const offset = (found.year - year) * 12 + (found.month - month);
    if (offset === 0) {
      return days - (found.day - 1);
    }
    days = offset < 0 ? days + (31 - found.day) : days - found.day;
  }
  throw new Error(`No first day found for ${year}-${month} (${calendar})`);
}

/** The day count of a Hijri date, or null if that day does not exist (30 of a 29-day month). */
export function fromHijri(
  date: HijriDate,
  calendar: HijriCalendar
): number | null {
  const { year, month, day } = date;
  if (
    !(
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day)
    )
  ) {
    return null;
  }
  if (year < 1 || year > MAX_HIJRI_YEAR || month < 1 || month > 12) {
    return null;
  }
  if (day < 1 || day > 30) {
    return null;
  }
  const days = firstOfMonth(year, month, calendar) + day - 1;
  const check = toHijri(days, calendar);
  return check.month === month && check.year === year ? days : null;
}

function nextMonth(year: number, month: number): [number, number] {
  return month === 12 ? [year + 1, 1] : [year, month + 1];
}

/** 29 or 30. */
export function monthLength(
  year: number,
  month: number,
  calendar: HijriCalendar
): number {
  const [nextYear, next] = nextMonth(year, month);
  return (
    firstOfMonth(nextYear, next, calendar) - firstOfMonth(year, month, calendar)
  );
}

export interface HijriMonthSpan {
  month: number;
  start: number;
  length: number;
}

/** Every month of a Hijri year with its first day and length. */
export function monthsOfYear(
  year: number,
  calendar: HijriCalendar
): HijriMonthSpan[] {
  const starts: number[] = [];
  for (let month = 1; month <= 12; month++) {
    starts.push(firstOfMonth(year, month, calendar));
  }
  starts.push(firstOfMonth(year + 1, 1, calendar));
  return starts.slice(0, 12).map((start, index) => ({
    month: index + 1,
    start,
    length: starts[index + 1] - start,
  }));
}

/** The first and last day of a Hijri year (354 or 355 days). */
export function yearSpan(
  year: number,
  calendar: HijriCalendar
): { start: number; end: number; length: number } {
  const start = firstOfMonth(year, 1, calendar);
  const next = firstOfMonth(year + 1, 1, calendar);
  return { start, end: next - 1, length: next - start };
}

export function inUmmAlQuraTable(year: number): boolean {
  return year >= UMM_AL_QURA_FIRST_YEAR && year <= UMM_AL_QURA_LAST_YEAR;
}

/**
 * The Julian-calendar date of a day count (Richards' algorithm via the Julian Day Number).
 * Historians give dates before 15 October 1582 in this calendar.
 */
export function julianFromDays(days: number): CivilDate {
  const julianDayNumber = days + 2_440_588;
  const c = julianDayNumber + 32_082;
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  return {
    day: e - Math.floor((153 * m + 2) / 5) + 1,
    month: m + 3 - 12 * Math.floor(m / 10),
    year: d - 4800 + Math.floor(m / 10),
  };
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` as a day count when it is a real date from 1 Muharram AH 1 to 9999. */
export function parseGregorianDate(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const days = daysFromCivil(year, month, day);
  const back = civilFromDays(days);
  if (back.month !== month || back.day !== day) {
    return null;
  }
  return days >= HIJRI_EPOCH_DAYS && days <= LAST_SUPPORTED_DAYS ? days : null;
}
